import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BillingSetupTab from './components/Billing/BillingSetupTab';
import FinanceSectionPage from './components/FinanceSectionPage';
import { UnsavedChangesProvider } from './components/Billing/UnsavedChanges';
import { BillingSetupSubTab } from '../../common/enums/common.enum';
import { FINANCE_SETTINGS } from '../../common/constants/routes.constants';

/**
 * How billing is configured: what the invoice says, what may be charged, and what each
 * group is charged.
 *
 * This used to be a tab on the kindergarten, beside its locations, teachers and children.
 * It never belonged there: those describe the kindergarten, while these three decide what
 * the Finance section produces, and an admin reaching for them is thinking about money,
 * not about the nursery's structure.
 *
 * The selected section is in the URL, so a reload or a shared link opens the one that was
 * being looked at rather than always landing on Settings.
 */
const FinanceSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const rawTab = Number(new URLSearchParams(location.search).get('tab'));
  const subTab: BillingSetupSubTab = Object.values(BillingSetupSubTab).includes(rawTab)
    ? (rawTab as BillingSetupSubTab)
    : BillingSetupSubTab.Settings;

  const setSubTab = (next: BillingSetupSubTab) => {
    // only the kindergarten carries over: search, filters and page belong to the section being left
    const params = new URLSearchParams();
    const kindergartenId = new URLSearchParams(location.search).get('kindergartenId');
    if (kindergartenId) params.set('kindergartenId', kindergartenId);
    params.set('tab', String(next));
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  return (
    <UnsavedChangesProvider>
      {guard => (
        <FinanceSectionPage
          title={t('nav.financeItems.settings')}
          description={t('finance.pages.settingsDescription')}
          messages={{ pickKindergarten: t('billing.setup.pickKindergarten'), invoicingOff: t('billing.setup.invoicingOff') }}
          // the same section for whichever kindergarten is picked, so switching stays here
          kindergartenHref={id => `${FINANCE_SETTINGS}?kindergartenId=${id}&tab=${subTab}`}
          guardNavigation={guard}
        >
          {({ kindergartenId }) => (
            <BillingSetupTab
              kindergartenId={kindergartenId}
              subTab={subTab}
              // unsaved settings are asked about before another part replaces them
              onSubTabChange={next => next !== subTab && guard(() => setSubTab(next))}
            />
          )}
        </FinanceSectionPage>
      )}
    </UnsavedChangesProvider>
  );
};

export default FinanceSettingsPage;
